import { useState, useEffect } from 'react';
import { ArrowLeft, Power, Clock, Mail, AlertCircle, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function MonitoreoEmails() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);

  useEffect(() => {
    loadConfig();
    const interval = setInterval(loadConfig, 5000); // Actualizar cada 5 segundos
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Calcular tiempo restante
    if (config?.enabled && config?.next_check) {
      const updateTimer = () => {
        const now = new Date();
        const nextCheck = new Date(config.next_check);
        const diff = nextCheck - now;

        if (diff > 0) {
          const minutes = Math.floor(diff / 60000);
          const seconds = Math.floor((diff % 60000) / 1000);
          setTimeRemaining({ minutes, seconds });
        } else {
          setTimeRemaining({ minutes: 0, seconds: 0 });
        }
      };

      updateTimer();
      const timer = setInterval(updateTimer, 1000);
      return () => clearInterval(timer);
    } else {
      setTimeRemaining(null);
    }
  }, [config]);

  const loadConfig = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/emails/monitoring/status`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setConfig(data);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async () => {
    setToggling(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/emails/monitoring/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          enabled: !config?.enabled,
          interval_minutes: 10
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        loadConfig();
      } else {
        toast.error('Error al cambiar estado del monitoreo');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error de conexión');
    } finally {
      setToggling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
        <div className="text-yellow-500">Cargando...</div>
      </div>
    );
  }

  const isEnabled = config?.enabled || false;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black p-6">
      <div className="max-w-4xl mx-auto">
        {/* Botón Volver */}
        <Button
          onClick={() => navigate('/dashboard')}
          variant="outline"
          className="border-gray-700 text-gray-300 hover:bg-gray-800 mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver al Dashboard
        </Button>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            Monitoreo Automático de Emails
          </h1>
          <p className="text-gray-400">
            Controla el sistema de respuestas automáticas a emails de retiro
          </p>
        </div>

        {/* Card de Estado */}
        <Card className={`mb-6 ${isEnabled ? 'bg-green-500/10 border-green-500/30' : 'bg-gray-800/50 border-gray-700'}`}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  isEnabled ? 'bg-green-500/20 border-2 border-green-500' : 'bg-gray-700'
                }`}>
                  <Power className={`w-6 h-6 ${isEnabled ? 'text-green-500' : 'text-gray-400'}`} />
                </div>
                <div>
                  <CardTitle className="text-white flex items-center gap-3">
                    Sistema de Monitoreo
                    <Badge className={isEnabled ? 'bg-green-500 text-white' : 'bg-gray-600 text-gray-300'}>
                      {isEnabled ? '● ACTIVO' : '○ INACTIVO'}
                    </Badge>
                  </CardTitle>
                  <CardDescription className="text-gray-400 mt-1">
                    {isEnabled 
                      ? 'Revisando emails automáticamente cada 10 minutos' 
                      : 'El monitoreo está desactivado'}
                  </CardDescription>
                </div>
              </div>
              
              <Button
                onClick={handleToggle}
                disabled={toggling}
                className={isEnabled 
                  ? 'bg-red-500 hover:bg-red-600 text-white'
                  : 'bg-green-500 hover:bg-green-600 text-white'
                }
              >
                <Power className="w-4 h-4 mr-2" />
                {toggling ? 'Cambiando...' : (isEnabled ? 'Desactivar' : 'Activar')}
              </Button>
            </div>
          </CardHeader>
        </Card>

        {/* Timer - Solo si está activo */}
        {isEnabled && timeRemaining && (
          <Card className="bg-blue-500/10 border-blue-500/30 mb-6">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Clock className="w-8 h-8 text-blue-400" />
                  <div>
                    <p className="text-white font-semibold text-lg">Próxima Revisión</p>
                    <p className="text-gray-400 text-sm">
                      {config.last_check 
                        ? `Última revisión: ${new Date(config.last_check).toLocaleTimeString('es-ES')}`
                        : 'Esperando primera revisión...'}
                    </p>
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-yellow-500 font-mono">
                    {String(timeRemaining.minutes).padStart(2, '0')}:{String(timeRemaining.seconds).padStart(2, '0')}
                  </div>
                  <p className="text-gray-500 text-xs mt-1">minutos restantes</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Información del Sistema */}
        <Card className="bg-gray-800/50 border-gray-700 mb-6">
          <CardHeader>
            <CardTitle className="text-white">Configuración Actual</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-900/50 p-4 rounded-lg">
                <p className="text-gray-400 text-sm mb-1">Email Monitoreado</p>
                <p className="text-white font-semibold">desactivate@kompa.global</p>
                <p className="text-gray-500 text-xs mt-1">→ retiroskompa@gmail.com</p>
              </div>
              <div className="bg-gray-900/50 p-4 rounded-lg">
                <p className="text-gray-400 text-sm mb-1">Intervalo de Revisión</p>
                <p className="text-white font-semibold">{config?.interval_minutes || 10} minutos</p>
              </div>
              <div className="bg-gray-900/50 p-4 rounded-lg">
                <p className="text-gray-400 text-sm mb-1">Fecha de Inicio</p>
                <p className="text-white font-semibold">
                  {config?.started_at 
                    ? new Date(config.started_at).toLocaleString('es-ES')
                    : 'No iniciado'}
                </p>
              </div>
              <div className="bg-gray-900/50 p-4 rounded-lg">
                <p className="text-gray-400 text-sm mb-1">Última Revisión</p>
                <p className="text-white font-semibold">
                  {config?.last_check 
                    ? new Date(config.last_check).toLocaleString('es-ES')
                    : 'Nunca'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Instrucciones */}
        <Card className="bg-gray-800/50 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-500" />
              Cómo Funciona
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-300">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm">
                El sistema revisa cada <strong className="text-white">10 minutos</strong> si hay emails nuevos en <strong className="text-yellow-500">desactivate@kompa.global</strong>
              </p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm">
                Solo procesa emails recibidos <strong className="text-white">después</strong> de activar el monitoreo (evita procesar emails antiguos)
              </p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm">
                Verifica contra la <strong className="text-white">blacklist de emails</strong> y <strong className="text-white">cédulas</strong> antes de responder
              </p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm">
                Envía respuesta automática desde <strong className="text-yellow-500">desactivate@kompa.global</strong> con el link al Formulario 1
              </p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm">
                Registra todos los emails procesados en <strong className="text-white">Email Tracking</strong>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
